<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useListStoreNew } from "@/stores/ListStoreNew";
import { usePagination } from "@/composables/usePagination";
import PrevNextButtons from "@/components/PrevNextButtons.vue";
import TimeAgo from "@/components/TimeAgo.vue";
import { useBreadcrumbs } from "@/composables/useBreadcrumbs";
import apiClient from "@/apiClient";

const list = useListStoreNew();
const { setBreadcrumb } = useBreadcrumbs();
const route = useRoute();

const searchTerm = ref("");
const perPageOptions = [25, 50, 100];
const perPage = ref(50);
const selectedUserId = ref("");
const userOptions = ref([]);
const userLoading = ref(false);

const { setUrl, changePage } = usePagination(list, null, () => ({
  per_page: perPage.value,
  search: searchTerm.value || undefined,
  user_id: selectedUserId.value || undefined,
}));

const applyFilters = () => {
  list.clearFilters();
  if (searchTerm.value) {
    list.addFilter("search", encodeURIComponent(searchTerm.value));
  }
  if (selectedUserId.value) {
    list.addFilter("user_id", selectedUserId.value);
  }
  list.addFilter("per_page", perPage.value);
};

const runSearch = () => {
  applyFilters();
  list.resetPage();
  setUrl();
};

const resetFilters = () => {
  searchTerm.value = "";
  selectedUserId.value = "";
  runSearch();
};

const formatDuration = (value) => {
  if (value === null || value === undefined) {
    return "-";
  }
  const ms = Number(value);
  if (Number.isNaN(ms)) {
    return "-";
  }
  if (ms < 1000) {
    return `${ms} ms`;
  }
  const seconds = ms / 1000;
  if (seconds < 60) {
    return `${seconds.toFixed(1)} s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.round(seconds % 60);
  return `${minutes}m ${remainder}s`;
};

const userLabel = (user) => {
  const name = user?.name || "Unknown user";
  const email = user?.email || "";
  return email ? `${name} - ${email}` : name;
};

const loadUsers = async () => {
  userLoading.value = true;
  try {
    const { data } = await apiClient.get("/api/analytics/pageviews/users");
    userOptions.value = data ?? [];
  } catch (error) {
    console.error(error);
    userOptions.value = [];
  } finally {
    userLoading.value = false;
  }
};

const rows = computed(() => list.getIndex);
const isLoading = computed(() => list.isLoading);
const showPrev = computed(() => list.showPrev);
const showNext = computed(() => list.showNext);

onMounted(() => {
  const searchQuery = route.query?.search;
  if (typeof searchQuery === "string") {
    searchTerm.value = searchQuery;
  }
  const userQuery = route.query?.user_id;
  if (typeof userQuery === "string") {
    selectedUserId.value = userQuery;
  }
  const perPageQuery = Number(route.query?.per_page);
  if (Number.isFinite(perPageQuery) && perPageOptions.includes(perPageQuery)) {
    perPage.value = perPageQuery;
  }

  list.configure({ remote: "/api/analytics/pageviews", local: "/super/page-views" });
  applyFilters();
  list.fetchIndex();
  loadUsers();
  setBreadcrumb("Page Views");
});

watch(perPage, (newValue, oldValue) => {
  if (newValue === oldValue) {
    return;
  }
  runSearch();
});
</script>

<template>
  <div class="space-y-6">
    <div class="flex flex-wrap items-start justify-between gap-4">
      <div>
        <p class="text-xs uppercase tracking-[0.25em] text-slate-400">Audit Log</p>
        <h1 class="text-2xl font-semibold text-slate-900 dark:text-slate-100">Page Views</h1>
        <p class="mt-2 text-sm text-slate-500">
          Track which pages were viewed, by whom, and when.
        </p>
      </div>
    </div>

    <section class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div class="flex flex-wrap items-end gap-3">
        <div class="flex-1 min-w-[220px]">
          <label class="text-xs uppercase tracking-wide text-slate-500">Search</label>
          <input
            v-model="searchTerm"
            class="input w-full px-3 py-2 dark:bg-slate-900 dark:text-slate-100 dark:placeholder-slate-400 dark:border-slate-700"
            placeholder="User, page, or referrer"
            @keyup.enter="runSearch"
          />
        </div>
        <div class="min-w-[220px]">
          <label class="text-xs uppercase tracking-wide text-slate-500">User</label>
          <select
            v-model="selectedUserId"
            class="input w-full px-3 py-2"
            :disabled="userLoading"
          >
            <option value="">All users</option>
            <option v-for="user in userOptions" :key="user.id" :value="String(user.id)">
              {{ userLabel(user) }}
            </option>
          </select>
        </div>
        <div class="min-w-[120px]">
          <label class="text-xs uppercase tracking-wide text-slate-500">Per page</label>
          <select v-model="perPage" class="input w-full px-3 py-2">
            <option v-for="option in perPageOptions" :key="option" :value="option">
              {{ option }}
            </option>
          </select>
        </div>
        <button class="btn accent h-10 px-4" type="button" @click="runSearch">
          Apply
        </button>
        <button class="btn h-10 px-4" type="button" @click="resetFilters">
          Clear
        </button>
      </div>
    </section>

    <section class="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div v-if="isLoading" class="px-6 py-6 text-sm text-slate-500 dark:text-slate-300">
        Loading page views...
      </div>
      <div v-else-if="!rows.length" class="px-6 py-6 text-sm text-slate-500 dark:text-slate-300">
        No page views found.
      </div>
      <ul v-else class="divide-y divide-slate-200 dark:divide-slate-700">
        <li v-for="entry in rows" :key="entry.id" class="px-6 py-4">
          <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div class="min-w-0 space-y-1">
              <div class="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                <span>{{ entry.user?.name ?? "Unknown user" }}</span>
                <span v-if="entry.user?.email" class="text-xs font-normal text-slate-400">
                  {{ entry.user.email }}
                </span>
              </div>
              <div class="text-sm text-slate-700 dark:text-slate-200">
                <span v-if="entry.title">{{ entry.title }}</span>
                <span v-else>{{ entry.path }}</span>
              </div>
              <div v-if="entry.title" class="text-xs text-slate-400 truncate">
                {{ entry.path }}
              </div>
              <div class="flex flex-wrap gap-3 text-xs text-slate-400">
                <span v-if="entry.duration_ms !== null && entry.duration_ms !== undefined">
                  Duration: {{ formatDuration(entry.duration_ms) }}
                </span>
                <span v-if="entry.ip">IP: {{ entry.ip }}</span>
                <span v-if="entry.referrer">Referrer: {{ entry.referrer }}</span>
              </div>
            </div>
            <div class="shrink-0 text-xs text-slate-500 dark:text-slate-400">
              <TimeAgo :data="entry.viewed_at" />
            </div>
          </div>
        </li>
      </ul>
    </section>

    <PrevNextButtons
      :showPrev="showPrev"
      :showNext="showNext"
      @prev="changePage(-1)"
      @next="changePage(1)"
    />
  </div>
</template>
